import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {AdyenPaymentMethodsService} from '../services/payment-methods'
import {paymentMethodsConfiguration} from '../components/paymentMethodsConfiguration'
import {AdyenEnvironmentService} from '../services/environment'
import {onPaymentsDetailsSuccess, onPaymentsSuccess} from './helper'
import {PAYMENT_METHOD_TYPES} from '../../../api/utils/constants'
import {AdyenShippingMethodsService} from '../services/shipping-methods'
import CustomApi from '../../../hooks/use-custom-api.js'
import {useCommerceApi} from '@salesforce/commerce-sdk-react'

export const AdyenCheckoutContext = React.createContext({})
const KLARNA_PAYMENT_METHOD_TYPES = [
    PAYMENT_METHOD_TYPES.KLARNA,
    PAYMENT_METHOD_TYPES.KLARNA_ACCOUNT
]

export const fetchShippingMethods = async (basketId, site, authToken, isBopis) => {
    const adyenShippingMethodsService = new AdyenShippingMethodsService(authToken, site)
    try {
        return await adyenShippingMethodsService.getShippingMethods(basketId, isBopis)
    } catch (error) {
        return null
    }
}

const AdyenCheckoutProvider = ({
    children,
    authToken,
    customerId,
    isCustomerRegistered,
    locale,
    site,
    adyenConfig,
    navigate,
    basket,
    returnUrl,
    page,
    isBopis,
    isBasketLoading = false,
    isEmployee,
    isCustomerGroupsLoading,
    adyenSelectedPaymentMethod,
    setAdyenSelectedPaymentMethod
}) => {
    const [fetchingPaymentMethods, setFetchingPaymentMethods] = useState(false)
    const [adyenPaymentMethods, setAdyenPaymentMethods] = useState()
    const [adyenEnvironment, setAdyenEnvironment] = useState()
    const [adyenStateData, setAdyenStateData] = useState()
    const [orderNo, setOrderNo] = useState()
    const [adyenPaymentInProgress, setAdyenPaymentInProgress] = useState()
    const [shippingMethods, setShippingMethods] = useState()
    const callPaymentMethodsOnPages = ['checkout']
    const customapi = new CustomApi()
    const api = useCommerceApi()
    const {proxy} = api.shopperExperience.clientConfig
    useEffect(() => {
        const fetchEnvironment = async () => {
            const adyenEnvironmentService = new AdyenEnvironmentService(authToken, site)
            try {
                const data = await adyenEnvironmentService.fetchEnvironment()
                setAdyenEnvironment(data ? data : {error: true})
            } catch (error) {
                setAdyenEnvironment({error})
            }
        }
        fetchEnvironment()
    }, [])

    useEffect(() => {
        const fetchPaymentMethods = async () => {
            setFetchingPaymentMethods(true)
            const adyenPaymentMethodsService = new AdyenPaymentMethodsService(authToken, site)
            const hasGiftCertificateLineItems =
                !!basket?.giftCertificateItems?.length || !!basket?.giftCertificateLineItems?.length
            const response = await customapi.get(
                authToken,
                'express-checkout-availability',
                '/',
                {},
                proxy
            )
            const shouldBlockKlarna =
                !!isEmployee || hasGiftCertificateLineItems || !response?.klarnaWidgetEnabled
            try {
                const data = await adyenPaymentMethodsService.fetchPaymentMethods(
                    customerId,
                    locale
                )
                const filteredData =
                    shouldBlockKlarna && Array.isArray(data?.paymentMethods)
                        ? {
                              ...data,
                              paymentMethods: data.paymentMethods.filter(
                                  (paymentMethod) =>
                                      !KLARNA_PAYMENT_METHOD_TYPES.includes(paymentMethod?.type)
                              )
                          }
                        : data
                if (isEmployee && filteredData?.paymentMethods?.length) {
                    filteredData.paymentMethods = filteredData.paymentMethods.filter(
                        ({type}) =>
                            !['affirm', 'paypal'].includes(type) && !type.startsWith('klarna')
                    )
                }
                setAdyenPaymentMethods(filteredData ? filteredData : {error: true})
                setFetchingPaymentMethods(false)
            } catch (error) {
                setAdyenPaymentMethods({error})
                setFetchingPaymentMethods(false)
            }
        }

        if (
            !adyenPaymentMethods &&
            !fetchingPaymentMethods &&
            !isBasketLoading &&
            !isCustomerGroupsLoading &&
            callPaymentMethodsOnPages.includes(page)
        ) {
            fetchPaymentMethods()
        }
    }, [
        basket?.basketId,
        isBasketLoading,
        page,
        adyenPaymentMethods,
        fetchingPaymentMethods,
        isEmployee,
        customerId, // this is added so after login we still see methods
        isCustomerGroupsLoading
    ])
    useEffect(() => {
        const fetchShippingMethodsData = async () => {
            const shippingMethods = await fetchShippingMethods(
                basket?.basketId,
                site,
                authToken,
                isBopis
            )
            setShippingMethods(shippingMethods || {error: true})
        }

        if (basket && !shippingMethods) {
            fetchShippingMethodsData()
        }
    }, [basket])

    const getTranslations = () => {
        return adyenConfig?.translations && Object.hasOwn(adyenConfig?.translations, locale.id)
            ? adyenConfig?.translations
            : null
    }

    const getPaymentMethodsConfiguration = async ({
        beforeSubmit = [],
        afterSubmit = [],
        beforeAdditionalDetails = [],
        afterAdditionalDetails = [],
        onError
    }) => {
        return paymentMethodsConfiguration({
            additionalPaymentMethodsConfiguration: adyenConfig?.paymentMethodsConfiguration,
            paymentMethods: adyenPaymentMethods?.paymentMethods,
            isCustomerRegistered,
            token: authToken,
            site,
            basket,
            returnUrl,
            customerId,
            getAdditionalPaymentData: adyenConfig?.getAdditionalPaymentData,
            onError: adyenConfig?.onError || onError,
            onNavigate: navigate,
            afterSubmit: [
                ...afterSubmit,
                ...(adyenConfig?.afterSubmit || []),
                onPaymentsSuccess(navigate)
            ],
            beforeSubmit: [...beforeSubmit, ...(adyenConfig?.beforeSubmit || [])],
            afterAdditionalDetails: [
                ...afterAdditionalDetails,
                ...(adyenConfig?.afterAdditionalDetails || []),
                onPaymentsDetailsSuccess(navigate)
            ],
            beforeAdditionalDetails: [
                ...beforeAdditionalDetails,
                ...(adyenConfig?.beforeAdditionalDetails || [])
            ]
        })
    }

    const value = {
        adyenEnvironment,
        adyenPaymentMethods,
        adyenSelectedPaymentMethod,
        adyenStateData,
        orderNo,
        adyenPaymentInProgress,
        locale,
        navigate,
        shippingMethods,
        isBopis,
        basket,
        site,
        authToken,
        page,
        fetchShippingMethods,
        setAdyenPaymentInProgress: (data) => setAdyenPaymentInProgress(data),
        setAdyenStateData: (data) => setAdyenStateData(data),
        setOrderNo: (data) => setOrderNo(data),
        setAdyenSelectedPaymentMethod: (data) => setAdyenSelectedPaymentMethod(data),
        getPaymentMethodsConfiguration,
        getTranslations
    }

    return <AdyenCheckoutContext.Provider value={value}>{children}</AdyenCheckoutContext.Provider>
}

AdyenCheckoutProvider.propTypes = {
    children: PropTypes.any,
    authToken: PropTypes.string,
    customerId: PropTypes.string,
    isCustomerRegistered: PropTypes.bool,
    locale: PropTypes.any,
    site: PropTypes.any,
    adyenConfig: PropTypes.any,
    navigate: PropTypes.any,
    basket: PropTypes.any,
    returnUrl: PropTypes.string,
    page: PropTypes.oneOf(['checkout', 'confirmation', 'redirect']),
    isBasketLoading: PropTypes.bool,
    isEmployee: PropTypes.bool,
    isCustomerGroupsLoading: PropTypes.bool,
    isBopis: PropTypes.bool,
    setAdyenSelectedPaymentMethod: PropTypes.any,
    adyenSelectedPaymentMethod: PropTypes.any
}

export default AdyenCheckoutProvider
